/**
 * InternalNotes — a reusable, append-only internal-notes thread that attaches to
 * ANY entity (a maintenance plan, a dispatch, a board card, a job…). Each entry
 * auto-stamps the date/time AND the dispatcher's first name at the moment it's
 * written, so there's always a "who said this, and when" trail.
 *
 * Deliberately separate from pegasus_note (the customer-knowledge notes that feed
 * the Good-to-Know pills): these are INTERNAL operational notes — never shown to
 * customers, never mined by the AI. Drop <InternalNotes entityType entityId />
 * in wherever a person needs to jot something for the next person.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Lock, Send, Trash2 } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useCurrentUser } from "@/lib/currentUser";

export interface InternalNote {
  id: string;
  body: string;
  author_name: string | null;
  author_email: string | null;
  created_at: string;
}

export function useInternalNotes(entityType: string, entityId: string | null) {
  return useQuery({
    queryKey: ["internal_notes", entityType, entityId],
    enabled: !!entityId,
    queryFn: async (): Promise<InternalNote[]> => {
      const { data, error } = await supabase
        .from("pegasus_internal_note")
        .select("id, body, author_name, author_email, created_at")
        .eq("entity_type", entityType)
        .eq("entity_id", entityId!)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as InternalNote[];
    },
  });
}

function stampOf(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit",
  });
}

export function InternalNotes({
  entityType,
  entityId,
  title = "Internal Notes",
  placeholder = "Add an internal note for the team…",
}: {
  entityType: string;
  entityId: string | null;
  title?: string;
  placeholder?: string;
}) {
  const qc = useQueryClient();
  const { data: notes } = useInternalNotes(entityType, entityId);
  const { data: me } = useCurrentUser();
  const [body, setBody] = useState("");
  const inval = () => qc.invalidateQueries({ queryKey: ["internal_notes", entityType, entityId] });

  const add = useMutation({
    mutationFn: async (text: string) => {
      const { error } = await supabase.from("pegasus_internal_note").insert({
        entity_type: entityType,
        entity_id: entityId,
        body: text,
        author_name: me?.firstName ?? null,
        author_email: me?.email ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => { setBody(""); inval(); },
  });
  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("pegasus_internal_note").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: inval,
  });

  const submit = () => { const t = body.trim(); if (t && entityId) add.mutate(t); };
  const list = notes ?? [];

  return (
    <div className="rounded-xl border border-hairline bg-white p-4 shadow-e1">
      <div className="mb-2 flex items-center gap-2">
        <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-amber-50 text-amber-600"><Lock className="h-3.5 w-3.5" /></span>
        <span className="text-xs font-semibold uppercase tracking-wider text-slate-600">{title}</span>
        <span className="ml-auto text-[10px] font-medium uppercase tracking-wider text-slate-400">Internal only</span>
      </div>

      {/* Composer — stamps the date/time + your first name on post */}
      <div className="rounded-lg border border-hairline bg-slate-50/60 p-2">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); submit(); } }}
          rows={2}
          placeholder={placeholder}
          className="w-full resize-none rounded-md border border-hairline bg-white px-2.5 py-1.5 text-sm text-slate-700 focus:border-brand-400 focus:outline-none"
        />
        <div className="mt-1.5 flex items-center justify-between gap-2">
          <span className="text-[11px] text-slate-400">
            {me?.firstName ? <>Posts as <span className="font-medium text-slate-500">{me.firstName}</span> · stamped now</> : "Stamped with your name + the time"}
          </span>
          <button
            type="button"
            onClick={submit}
            disabled={!body.trim() || !entityId || add.isPending}
            className="inline-flex items-center gap-1.5 rounded-md bg-brand-600 px-2.5 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-brand-700 disabled:opacity-40"
          >
            <Send className="h-3.5 w-3.5" /> {add.isPending ? "Posting…" : "Post note"}
          </button>
        </div>
      </div>

      {/* Thread — newest first */}
      {list.length > 0 ? (
        <ul className="mt-3 space-y-2.5">
          {list.map((n) => (
            <li key={n.id} className="group rounded-lg border border-hairline bg-white px-3 py-2">
              <p className="whitespace-pre-wrap text-sm text-slate-700">{n.body}</p>
              <div className="mt-1 flex items-center gap-1.5 text-[11px] text-slate-400">
                <span className="font-medium text-slate-500">{n.author_name ?? "Unknown"}</span>
                <span>·</span>
                <span>{stampOf(n.created_at)}</span>
                {me?.email && n.author_email === me.email && (
                  <button
                    type="button"
                    onClick={() => remove.mutate(n.id)}
                    title="Delete this note"
                    className="ml-auto text-slate-300 opacity-0 transition-opacity hover:text-red-500 group-hover:opacity-100"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-3 text-xs italic text-slate-400">No internal notes yet.</p>
      )}
    </div>
  );
}
